<template>
  <div class="pa-4">
    <div
      v-if="loading"
      class="d-flex flex-column justify-center align-center"
      style="min-height: 300px"
    >
      <v-progress-circular color="primary" indeterminate></v-progress-circular>
      <span class="mt-4 grey--text text--darken-1 body-1"
        >Chargement des statistiques...</span
      >
    </div>

    <div v-else>
      <v-alert v-if="errorMessage" type="warning" dense text class="mb-4">
        {{ errorMessage }}
      </v-alert>

      <div
        v-if="!hasData && !errorMessage"
        class="text-center grey--text py-10"
      >
        Aucune donnée disponible pour cette sélection.
      </div>

      <div v-if="hasData">
        <v-row>
          <v-col cols="12" md="4">
            <v-card outlined class="fill-height elevation-1">
              <v-card-title class="subtitle-1 font-weight-bold">
                Par Origine
              </v-card-title>
              <v-card-text>
                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody>
                      <tr v-for="(item, i) in stats.by_origin" :key="i">
                        <td>{{ item.text }}</td>
                        <td class="text-right font-weight-bold">
                          {{ item.value }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card outlined class="fill-height elevation-1">
              <v-card-title class="subtitle-1 font-weight-bold">
                Par Statut
              </v-card-title>
              <v-card-text>
                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody>
                      <tr v-for="(item, i) in stats.by_status" :key="i">
                        <td>
                          <v-chip
                            x-small
                            :color="getStatusColor(item.text)"
                            dark
                            label
                          >
                            {{ item.text }}
                          </v-chip>
                        </td>
                        <td class="text-right font-weight-bold">
                          {{ item.value }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card outlined class="fill-height elevation-1">
              <v-card-title class="subtitle-1 font-weight-bold">
                Par Site (Top 10)
              </v-card-title>
              <v-card-text>
                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody>
                      <tr v-for="(item, i) in topSites" :key="i">
                        <td
                          class="text-truncate"
                          style="max-width: 150px"
                          :title="item.text"
                        >
                          {{ item.text }}
                        </td>
                        <td class="text-right font-weight-bold">
                          {{ item.value }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-6">
          <v-col cols="12">
            <v-card outlined class="elevation-1">
              <v-card-title class="subtitle-1 font-weight-bold">
                {{ chartTitle }}
              </v-card-title>
              <v-card-text>
                <div
                  v-if="chartData.series.length === 0"
                  class="text-center py-6 grey--text"
                >
                  Pas de données pour le graphique sur cette période.
                </div>
                <generic-graph
                  v-else
                  type="bar"
                  :series="displayedSeries"
                  :chartCategories="chartData.categories"
                  :chartOptions="chartOptions"
                  height="400"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@axios";
import GenericGraph from "@/components/common/charts/GenericGraph.vue";
import { ref, watch, computed, onMounted } from "@vue/composition-api";
import store from "@/store";

export default {
  name: "AifPostGenerationStats",
  components: { GenericGraph },
  props: {
    params: {
      type: Object,
      default: () => ({}),
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const loading = ref(false);
    const errorMessage = ref(null);
    const hasData = ref(false);
    const isolatedSeriesIndex = ref(null);

    const stats = ref({
      by_origin: [],
      by_status: [],
      by_site: [],
    });

    const chartData = ref({
      categories: [],
      series: [],
    });

    const displayedSeries = computed(function () {
      var series = chartData.value.series;
      if (!series || series.length === 0) {
        return series;
      }
      if (isolatedSeriesIndex.value === null) {
        return series;
      }
      return series.map(function (s, i) {
        if (i === isolatedSeriesIndex.value) {
          return s;
        }
        return {
          name: s.name,
          data: s.data.map(function () {
            return null;
          }),
        };
      });
    });

    const chartTitle = computed(function () {
      var series = chartData.value.series;
      if (
        isolatedSeriesIndex.value === null ||
        !series ||
        !series[isolatedSeriesIndex.value]
      ) {
        return "Évolution par Site (Empilé) - tous les sites";
      }
      return (
        "Évolution par Site (Empilé) - " +
        series[isolatedSeriesIndex.value].name
      );
    });

    var handleLegendClick = function (seriesIndex) {
      if (isolatedSeriesIndex.value === seriesIndex) {
        isolatedSeriesIndex.value = null;
      } else {
        isolatedSeriesIndex.value = seriesIndex;
      }
    };

    const chartOptions = {
      chart: {
        stacked: true,
        toolbar: { show: true },
        zoom: { enabled: true },
        events: {
          legendClick(chartContext, seriesIndex) {
            setTimeout(() => {
              handleLegendClick(seriesIndex);
            }, 0);
            return false;
          },
        },
      },

      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "50%",
          dataLabels: {
            position: "top",
            total: {
              enabled: true,
              offsetY: -4,
              style: {
                fontSize: "11px",
                fontWeight: 600,
              },
              formatter: function (total, opts) {
                return total;
              },
            },
          },
        },
      },

      dataLabels: {
        enabled: true,
      },

      xaxis: {
        type: "category",
        labels: {
          rotate: -45,
          rotateAlways: true,
          hideOverlappingLabels: false,
          trim: false,
          style: { fontSize: "12px" },
        },
      },

      legend: {
        position: "bottom",
        offsetY: 5,
        onItemClick: {
          toggleDataSeries: false,
        },
      },

      fill: {
        opacity: 1,
      },
    };

    const topSites = computed(() => {
      const sorted = [...stats.value.by_site].sort((a, b) => b.value - a.value);
      return sorted.slice(0, 10);
    });

    const getStatusColor = (status) => {
      switch (status) {
        case "processing":
          return "info";
        case "published":
        case "success":
          return "success";
        case "error":
          return "error";
        default:
          return "grey";
      }
    };

    const loadStats = async () => {
      // Si le panneau n'est pas visible, on ne fait rien (sécurité)
      if (!props.isVisible) return;

      loading.value = true;
      errorMessage.value = null;

      try {
        const [countRes, chartRes] = await Promise.all([
          axios.get("/api/ai-factory/stats/counts", {
            params: props.params,
          }),
          axios.get("/api/ai-factory/stats/chart", {
            params: props.params,
          }),
        ]);

        stats.value = countRes.data;
        chartData.value = chartRes.data;
        isolatedSeriesIndex.value = null;
        hasData.value = true;
      } catch (error) {
        console.error("Erreur chargement stats:", error);
        if (error.response && error.response.status === 400) {
          errorMessage.value =
            error.response.data.detail || "Erreur de paramètres.";
          chartData.value = { categories: [], series: [] };
        } else {
          errorMessage.value = "Erreur technique lors du chargement.";
        }
      } finally {
        loading.value = false;
      }
    };

    // --- WATCHERS & HOOKS ---

    // 1. Déclenchement à l'ouverture du panneau (false -> true)
    watch(
      () => props.isVisible,
      (newVal) => {
        if (newVal === true) {
          loadStats();
        }
      }
    );

    // 2. Déclenchement au changement de filtre (seulement si visible)
    watch(
      () => props.params,
      () => {
        if (props.isVisible) {
          loadStats();
        }
      },
      { deep: true }
    );

    // 3. Déclenchement initial si le panneau est déjà ouvert au chargement (ex: rechargement de page avec état conservé)
    onMounted(() => {
      if (props.isVisible) {
        loadStats();
      }
    });

    return {
      loading,
      stats,
      chartData,
      chartOptions,
      displayedSeries,
      chartTitle,
      topSites,
      errorMessage,
      getStatusColor,
      hasData,
    };
  },
};
</script>
